import { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../context/auth/AuthContext";
import useTranslator from "../hooks/useTranslator";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  getUserDisplayName,
  haveValue,
  placeNotificationLink,
  toast,
} from "../helper/common";
import LandLordContext from "../context/landlord/LandLordContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  calculateNowTime,
  getTenantShareRequests,
} from "../services/sharedService";
import { Scrollbar, Mousewheel } from "swiper/modules";
import SkorDefaultLoader from "../assets/img/skorDefaultLoader.gif";
import WelcomeModal from "../components/Workspace/WelcomeModal";
import PendingApplicationsModal from "../components/Workspace/PendingApplicationsModal";
import { useCookies } from "react-cookie";
import TenantContext from "../context/tenant/TenantContext";

const WorkspaceDashboard = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const loadingRef = useRef(false);
  const { toggleNewWorkspaceModal, notificationSeen, user, loadUserTenant } =
    authContext;
  const { shareProfile } = useContext(TenantContext);

  const landlordContext = useContext(LandLordContext);
  const {
    fetchAllLandlordNotification,
    fetchWorkspacesOverview,
    acceptWorkspaceInvite,
    getBasicWorkspaceDetails,
  } = landlordContext;

  const [cookies, setCookie, removeCookie] = useCookies([
    "workspace_invite",
    "sharedProfileLink",
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [groupedNotifications, setGroupedNotifications] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [workspaceJoiningLoading, setWorkspaceJoiningLoading] = useState(
    haveValue(cookies.workspace_invite)
  );
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [allNotifications, setAllNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [total, setTotal] = useState(0);
  const [tempWorkspaceData, setTempWorkspaceData] = useState({});
  const [openWelcomeWorkspaceModal, setOpenWelcomeWorkspaceModal] =
    useState(false);
  const [showPendingApplicationsModal, setShowPendingApplicationsModal] =
    useState(false);
  const [pendingApplicationsData, setPendingApplicationsData] = useState([]);
  const [pendingApplicationsLoading, setPendingApplicationsLoading] =
    useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const importInitiatedRef = useRef(false);

  useEffect(() => {
    const shouldOpen =
      searchParams.get("new_application") === "true" ||
      searchParams.get("new_application") === "1";

    getTenantShareRequests()
      .then((res) => {
        if (shouldOpen) {
          setShowPendingApplicationsModal(true);
        }
        const data =
          res?.success_res?.applications || res?.success_res || res?.data || [];
        setPendingApplicationsData(Array.isArray(data) ? data : []);
        setPendingRequestsCount(Array.isArray(data) ? data.length : 0);
      })
      .catch((err) => {
        console.log(err);
        toast.error(_t("errorWhileFetchingDashboardNotifications"));
      })
      .finally(() => {
        setPendingApplicationsLoading(false);
      });
  }, [searchParams]);

  const closePendingApplicationsModal = () => {
    setShowPendingApplicationsModal(false);
    const nextSearch = new URLSearchParams(searchParams);
    nextSearch.delete("new_application");
    setSearchParams(nextSearch, { replace: true });
  };

  useEffect(() => {
    if (allNotifications?.length > 0) {
      const groupedData = groupNotificationsByDate(allNotifications);
      setGroupedNotifications(groupedData);
    }
  }, [allNotifications]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
          document.body.offsetHeight - 500 &&
        !loadingRef.current &&
        allNotifications.length < total
      ) {
        loadData(page + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, total]);

  const groupNotificationsByDate = (notifications) => {
    return notifications.reduce((acc, item) => {
      const date = new Date(item.createdAt);

      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en-GB", { month: "long" });
      const year = date.getFullYear();

      const dateKey = `${day} ${month}, ${year}`;

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(item);
      return acc;
    }, {});
  };

  const loadData = async (currentPage) => {
    try {
      setLoading(true);
      if (loadingRef.current) return;
      loadingRef.current = true;

      const notificationRes = await fetchAllLandlordNotification(currentPage);

      setAllNotifications((prev) => [
        ...prev,
        ...(notificationRes?.notifications || []),
      ]);
      setUnreadCount(notificationRes?.unread_notifications || 0);
      setTotal(notificationRes?.total_notifications);
      setPage(notificationRes?.current_page);
    } catch (err) {
      console.log(err, "");
      toast.error(_t("errorWhileFetchingDashboardNotifications"));
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const importProfileRedirect = async () => {
    if (importInitiatedRef.current) return;

    let userid = localStorage.getItem("saveduserid");
    const sharedProfileLink = cookies.sharedProfileLink;

    if (haveValue(sharedProfileLink)) {
      importInitiatedRef.current = true;
      const shareProfileResponse = await shareProfile(
        sharedProfileLink,
        userid,
        "null",
        "full",
        { shareType: "tLink" }
      );
      removeCookie("sharedProfileLink", { path: "/" });

      if (shareProfileResponse?.success) {
        toast.success(_t("profileImported"));
      } else if (shareProfileResponse?.key === "profile-not-found") {
        toast.error(shareProfileResponse?.errormsg ?? _t("failedToGetProfile"));
        return;
      } else if (shareProfileResponse?.key === "access-cancelled") {
        toast.error(
          _t(shareProfileResponse?.errormsg) ?? _t("failedToGetProfile")
        );
        return;
      }
    }
  };

  useEffect(() => {
    if (haveValue(cookies.workspace_invite)) {
      setWorkspaceJoiningLoading(true);
      const temp_workspace_id = cookies.workspace_invite;
      acceptWorkspaceInvite({ workspaceId: temp_workspace_id })
        .then((res) => {
          console.log("Workspace invite accepted.");
          removeCookie("workspace_invite", {
            path: "/", // ✅ MUST MATCH,
            maxAge: 1800,
          });
          getBasicWorkspaceDetails({ workspaceId: temp_workspace_id }).then(
            (res) => {
              const data = res?.success_res?.data;
              setTempWorkspaceData(data);
              setOpenWelcomeWorkspaceModal(true);
            }
          );
          loadUserTenant("landlord");
        })
        .catch((err) => {
          console.log(err, "Error while accepting workspace invite");
        })
        .finally(() => {
          setWorkspaceJoiningLoading(false);
        });
    }
    importProfileRedirect();

    getTenantShareRequests()
      .then((res) => {
        const data =
          res?.success_res?.applications || res?.success_res || res?.data || [];
        setPendingRequestsCount(Array.isArray(data) ? data.length : 0);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if ((user?.workspaceIds ?? [])?.length === 0) {
      return;
    }
    loadData();
    Promise.all([fetchWorkspacesOverview()])
      .then(([workspaceRes]) => {
        setAllWorkspaces(workspaceRes?.success_res);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [user?._id, user?.workspaceIds?.length]);

  useEffect(() => {
    if (swiperInstance && allWorkspaces?.length > 0) {
      swiperInstance.update();
    }
  }, [allWorkspaces, swiperInstance]);

  if (!(loading || workspaceJoiningLoading) && allWorkspaces?.length === 0) {
    return (
      <div className="workspace-dashboard-main workspace-dashboard-empty">
        <div className="workspace-empty-state">
          <div className="workspace-empty-icon">
            <img src="/images/briefcaseIcon.svg" alt="workspace" />
          </div>
          <h4>{_t("youDontHaveAWorkspaceYet")}</h4>
          <p>
            {_t(
              "createAWorkspaceToInviteTenantsReviewProfilesAndManageYourProperties"
            )}
          </p>
          <button
            className="workspace-empty-create-btn"
            onClick={() => toggleNewWorkspaceModal(true)}
          >
            + {_t("createYourWorkspace")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-dashboard-main">
      {/* ALL WORKSPACES */}
      <div className="all-workspace-container">
        <div className="d-flex align-items-center justify-content-between add-workspace-wrapper">
          <div className="d-flex align-items-center custom-gap-3">
            <div className="all-workspace-icon-wrapper d-flex align-items-center justify-content-center">
              <img src="/images/briefcaseIcon.svg" alt="Icon" />
            </div>
            <div className="all-workspace-text-wrapper">
              <h4>{_t("myWorkspaces")}</h4>
              <p>
                {user?.workspaceIds?.length} {_t("workspaces")}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center custom-gap-2">
            <button
              className="addNewWorkspaceButton d-flex align-items-center custom-gap-2"
              onClick={() => toggleNewWorkspaceModal(true)}
            >
              <img src="/images/add-white.svg" alt="" />
              {_t("addNewWorkspace")}
            </button>
          </div>
        </div>

        <div className="d-flex flex-nowrap workspace-info-card-wrapper">
          <Swiper
            key={allWorkspaces.length}
            slidesPerView={1.4}
            spaceBetween={18}
            grabCursor={allWorkspaces.length > 1}
            className="mySwiper"
            observer={true}
            observeSlideChildren={true}
            observeParents={true}
            updateOnImagesReady={true}
            modules={[Mousewheel, Scrollbar]}
            scrollbar={{
              draggable: true,
              hide: false,
            }}
            freeMode={true}
            mousewheel={{
              forceToAxis: true,
              sensitivity: 2.5,
              releaseOnEdges: false,
              thresholdDelta: 1,
            }}
            onSwiper={setSwiperInstance}
            breakpoints={{
              1280: { slidesPerView: 5.7 },
            }}
          >
            {allWorkspaces?.map((workspace) => (
              <SwiperSlide key={workspace?._id}>
                <div
                  className="workspace-info-card"
                  role="button"
                  onClick={() => {
                    loadUserTenant("landlord");
                    navigate(`/${workspace?.uuid}/profile-request`);
                  }}
                >
                  <div className="time-container d-flex align-items-start justify-content-end">
                    {workspace?.lastOpenedAt && (
                      <p>
                        {calculateNowTime(
                          workspace?.lastOpenedAt,
                          localStorage.getItem("i18nextLng")
                        )}
                      </p>
                    )}
                  </div>
                  <div
                    className={`workspace-image-container ${
                      !haveValue(workspace?.workspaceImage) ? "no-image" : ""
                    }`}
                  >
                    {haveValue(workspace?.workspaceImage) ? (
                      <img
                        src={`${process.env.REACT_APP_CDN_ENDPOINT}/${process.env.REACT_APP_CDN_CONTAINER}/${workspace?.workspaceImage}`}
                        alt=""
                      />
                    ) : (
                      <h4>{workspace.name?.substring(0, 1)}</h4>
                    )}
                  </div>
                  <h5 className="two-line-ellipsis">{workspace?.name}</h5>
                  <p>
                    {workspace?.memberCount} {_t("members")}
                  </p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* NOTIFICATION CONTAINER */}
      <div className="all-workspace-container notification-container">
        <div className="d-flex align-items-center justify-content-between add-workspace-wrapper">
          <div className="d-flex align-items-center custom-gap-3">
            <div className="all-workspace-icon-wrapper d-flex align-items-center justify-content-center">
              <img src="/images/bell-icon-black.svg" alt="Icon" />
            </div>
            <div className="all-workspace-text-wrapper">
              <h4>{_t("notifications")}</h4>
            </div>
          </div>
          <p className="unreadText">
            {unreadCount} {_t("unread")}
          </p>
        </div>
        {Object.keys(groupedNotifications).length > 0
          ? Object.entries(groupedNotifications)?.map(
              ([date, notifications], index, arr) => (
                <div
                  key={index}
                  className="notification-wrapper-main d-flex custom-gap-3"
                >
                  <div
                    className={`notification-info-time ${
                      index === arr.length - 1 ? "last-child" : ""
                    }`}
                  >
                    <h5>{date}</h5>
                    <p>
                      {notifications.filter((n) => !n.marked_read).length}{" "}
                      {_t("unread")}
                    </p>
                  </div>
                  <div className="d-flex flex-column notification-info-main">
                    {notifications.map((notification) => (
                      <div
                        className={`notification-info ${
                          !notification?.marked_read ? "unread" : "read"
                        }`}
                        role="button"
                        onClick={() => {
                          if (
                            notification?.shared_profile_id?.pendingDecision ===
                            true
                          ) {
                            toast.error(_t("pendingDecisionFromTenant"));
                            return;
                          }
                          notificationSeen(notification?._id);
                          navigate(
                            placeNotificationLink(
                              notification,
                              notification?.workspaceId?.uuid
                            )
                          );
                        }}
                      >
                        <h6>
                          {(() => {
                            const getTenantName = (tenant) =>
                              getUserDisplayName(tenant, "");
                            switch (notification?.notification_type) {
                              case "guarantor_removed":
                                const splited = notification?.body.split(" ");
                                return `${splited[0]} ${splited[1]} ${_t(
                                  "hasRemovedHimselfAsAGuarantorFor"
                                )} ${
                                  notification?.shared_profile_id?.tenant_fname
                                } ${
                                  notification?.shared_profile_id?.tenant_lname
                                }`;

                              case "guarantor_declined":
                                return `${_t("guarantorRequestDeclinedBy")} 
                                ${getTenantName(notification?.created_by_tenant)}
                                `;

                              case "property_assigned":
                                return `${
                                  notification?.created_by_landlord
                                    ?.fname_companyName
                                } ${_t("hasAwardedYouAProperty")}`;

                              case "profile_linked":
                                return `${
                                  notification?.created_by_landlord
                                    ?.fname_companyName
                                } ${_t("hasLinkedYourProfileToAAddress")}.`;

                              case "profile_sharing":
                                return `${getTenantName(
                                  notification?.created_by_tenant
                                )} ${_t("hasSharedProfileWithYou")}`;

                              case "profile_sharing_accepted":
                                //   body: `Tenant ${request.tenant_id.fname} ${request.tenant_id.lname} profile was shared with WS ${workspaceData.name}`,
                                return `Tenant ${getTenantName(
                                  notification?.created_by_tenant
                                )} ${_t("wasSharedWithWS")} ${notification.workspaceId.name}`;

                              case "document_updated":
                                return `${getTenantName(
                                  notification?.created_by_tenant
                                )} ${_t("hasUpdatedTheirDocuments")}`;

                              case "guarantor_document_updated":
                                return `${getTenantName(
                                  notification?.created_by_tenant
                                )}'s ${_t("guarantor")} ${_t(
                                  "hasUpdatedTheirDocuments"
                                )}`;

                              case "guarantor_added":
                                return `${getTenantName(
                                  notification?.created_by_tenant
                                )} ${_t("hasAddedNewGuarantor")}`;

                              case "financial_info_updated":
                                return `${getTenantName(
                                  notification?.created_by_tenant
                                )} ${_t("hasUpdatedTheirFinancialInformation")}`;

                              case "financial_info_updated_guarantor":
                                return `${getTenantName(
                                  notification?.created_by_tenant
                                )}'s guarantor ${_t(
                                  "hasUpdatedTheirFinancialInformation"
                                )}`;
                              case "profile_sharing_request":
                                return `${getTenantName(
                                  notification?.created_by_tenant
                                )} ${_t("hasRequestedSharedProfileWithYou")}`;
                              default:
                                console.log("DEBUG", notification);
                                return notification?.body;
                            }
                          })(notification)}
                        </h6>
                        <p>
                          {calculateNowTime(
                            notification?.createdAt,
                            localStorage.getItem("i18nextLng")
                          )}
                        </p>
                        {(notification?.workspaceId?.workspaceImage ||
                          notification?.workspaceId?.name) && (
                          <div className="d-flex align-items-center workspace-name-wrapper custom-gap-2">
                            {haveValue(
                              notification?.workspaceId?.workspaceImage
                            ) ? (
                              <img
                                src={`${process.env.REACT_APP_CDN_ENDPOINT}/${process.env.REACT_APP_CDN_CONTAINER}/${notification?.workspaceId?.workspaceImage}`}
                                alt=""
                              />
                            ) : (
                              notification?.workspaceId?.name && (
                                <h5 className="d-flex align-items-center justify-content-center">
                                  {notification?.workspaceId?.name?.substring(
                                    0,
                                    1
                                  )}
                                </h5>
                              )
                            )}
                            <h4>{notification?.workspaceId?.name}</h4>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            )
          : !loading && <div className="noData">{_t("noDataFound")}</div>}
        {(loading || workspaceJoiningLoading) && (
          <div className="defaultSkorLoaderParent mt-3">
            <img
              src={SkorDefaultLoader}
              className="defaultSkorLoaderSmall"
              alt="Example"
            />
          </div>
        )}
        {openWelcomeWorkspaceModal && (
          <WelcomeModal
            isOpen={openWelcomeWorkspaceModal}
            onClose={(e) => setOpenWelcomeWorkspaceModal(false)}
            workspaceData={tempWorkspaceData}
          />
        )}
        {/* <PendingApplicationsModal
          isOpen={showPendingApplicationsModal}
          onClose={closePendingApplicationsModal}
          loading={pendingApplicationsLoading}
          applications={pendingApplicationsData}
          workspaces={allWorkspaces}
        /> */}
      </div>
    </div>
  );
};

export default WorkspaceDashboard;
